export const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8088';
const BASE = BASE_URL;

function authHeader(): HeadersInit {
  const tok = localStorage.getItem('admin_token');
  return tok ? { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
}

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { ...opts, headers: { ...authHeader(), ...(opts.headers ?? {}) } });
  if (res.status === 401) { localStorage.removeItem('admin_token'); window.location.href = '/'; }
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error((e as any).error ?? `${res.status}`); }
  return res.json();
}

export interface SlipPayment {
  id: string;
  order_id: string;
  user_id: string;
  slip_url: string;
  status: string;
  note: string;
  created_at: string;
  updated_at: string;
  /** จากออเดอร์ที่ join — pending | paid | expired */
  order_status?: string;
  /** ชื่อแสดงใน LINE ตอนสั่ง */
  line_display_name?: string;
}

/** ออเดอร์หมดเวลาชำระแต่ไม่มีแถวใน miniapp_payments — จาก API เมื่อ order_status=expired */
export interface ExpiredOrderNoSlip {
  order_id: string;
  user_id: string;
  line_display_name?: string;
  created_at: string;
}

/** GET /admin/payments* แบบมี pagination */
export interface SlipListResponse {
  payments: SlipPayment[];
  total: number;
  page: number;
  limit: number;
  has_more: boolean;
  /** มีเมื่อ order_status=expired — ออเดอร์หมดเวลาโดยยังไม่อัปโหลดสลิป */
  expired_orders_no_slip?: ExpiredOrderNoSlip[];
}

/** Query กรองช่วงวันที่บันทึกสลิป (API admin) */
export type AdminSlipTimeParams = {
  since_days?: number;
  created_from?: string;
  created_to?: string;
  /** เรียงตามเวลาอัปโหลดสลิป — asc=เก่าก่อน, desc=ใหม่ก่อน */
  sort?: 'asc' | 'desc';
  /** แนบรายการออเดอร์หมดเวลายังไม่มีสลิป (expired_orders_no_slip) */
  include_expired_no_slip?: boolean;
};

/** GET /admin/stats/order-sales — ยอดขายจากออเดอร์ paid */
export type SalesByGroup = Record<string, number>;

export interface TopProductRow {
  name: string;
  qty: number;
}

export interface AdminOrderSalesWindow {
  order_count: number;
  cups: number;
  unique_users: number;
  /** จำนวนแก้วแยกตามประเภทเมนู (กาแฟ / ชา / น้ำหวาน / ขนม / …) — จาก API */
  by_group?: SalesByGroup;
  /** รายการเมนูขายดี เรียงจำนวนแก้ว (ชื่อตามออเดอร์) */
  top_products?: TopProductRow[];
}

export interface AdminOrderSalesResponse {
  since_1d: AdminOrderSalesWindow;
  since_7d: AdminOrderSalesWindow;
  all_time: AdminOrderSalesWindow;
}

/** เปิด/ปิดรับสั่ง — ซิงก์กับ Mini App */
export interface StoreStatusDTO {
  is_open: boolean;
  updated_at?: string;
}

export interface Promotion {
  id?: string;
  title: string;
  description: string;
  image_url: string;
  badge: string;
  valid_from?: string;
  valid_to: string;
}

export const adminApi = {
  login: (username: string, password: string) =>
    req<{ token: string; username: string }>('/admin/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  seed: (username: string, password: string) =>
    req<{ message: string }>('/admin/seed', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  getPending: (params?: {
    page?: number;
    limit?: number;
    order_status?: string;
    q?: string;
  } & AdminSlipTimeParams) => {
    const sp = new URLSearchParams();
    if (params?.page != null) sp.set('page', String(params.page));
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.order_status) sp.set('order_status', params.order_status);
    if (params?.q?.trim()) sp.set('q', params.q.trim());
    if (params?.since_days != null) sp.set('since_days', String(params.since_days));
    if (params?.created_from?.trim()) sp.set('created_from', params.created_from.trim());
    if (params?.created_to?.trim()) sp.set('created_to', params.created_to.trim());
    if (params?.sort === 'asc' || params?.sort === 'desc') sp.set('sort', params.sort);
    if (params?.include_expired_no_slip) sp.set('include_expired_no_slip', '1');
    const q = sp.toString();
    return req<SlipListResponse>(`/admin/payments/pending${q ? `?${q}` : ''}`);
  },

  getAll: (params?: {
    status?: string;
    order_status?: string;
    page?: number;
    limit?: number;
    q?: string;
  } & AdminSlipTimeParams) => {
    const sp = new URLSearchParams();
    if (params?.status) sp.set('status', params.status);
    if (params?.order_status) sp.set('order_status', params.order_status);
    if (params?.page != null) sp.set('page', String(params.page));
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.q?.trim()) sp.set('q', params.q.trim());
    if (params?.since_days != null) sp.set('since_days', String(params.since_days));
    if (params?.created_from?.trim()) sp.set('created_from', params.created_from.trim());
    if (params?.created_to?.trim()) sp.set('created_to', params.created_to.trim());
    if (params?.sort === 'asc' || params?.sort === 'desc') sp.set('sort', params.sort);
    if (params?.include_expired_no_slip) sp.set('include_expired_no_slip', '1');
    const q = sp.toString();
    return req<SlipListResponse>(`/admin/payments${q ? `?${q}` : ''}`);
  },

  verify: (paymentId: string, action: 'approve' | 'reject', note?: string) =>
    req<{ payment_id: string; status: string }>(`/admin/payments/${paymentId}/verify`, {
      method: 'PATCH',
      body: JSON.stringify({ action, note: note ?? '' }),
    }),

  createPromo: (promo: Promotion) =>
    req<Promotion>('/admin/promotions', { method: 'POST', body: JSON.stringify(promo) }),

  getOrderSalesStats: () => req<AdminOrderSalesResponse>('/admin/stats/order-sales'),

  getPublicStoreStatus: () => req<StoreStatusDTO>('/store/status'),

  patchStoreStatus: (isOpen: boolean) =>
    req<StoreStatusDTO>('/admin/store/status', {
      method: 'PATCH',
      body: JSON.stringify({ is_open: isOpen }),
    }),
};
